// src/components/SuperAdminSubscriptions.jsx
import React, { useEffect, useState, useContext } from "react";
import {
  collectionGroup,
  query,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { LanguageContext } from "../context/LanguageContext";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemText,
  Button,
  CircularProgress,
} from "@mui/material";

export default function SuperAdminSubscriptionsDesktop() {
  const { translate } = useContext(LanguageContext);

  // on garde juste un tableau d'objets "subscription"
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingCancel, setLoadingCancel] = useState(null);

  // Écoute en temps réel de tous les abonnements Firestore
  useEffect(() => {
    const subsQuery = query(collectionGroup(db, "subscriptions"));
    const unsub = onSnapshot(subsQuery, (snap) => {
      setSubscriptions(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          ref: docSnap.ref,
        }))
      );
    });
    return () => unsub();
  }, []);

  // Annulation d’un abonnement
  const handleCancel = async (subId) => {
    const sub = subscriptions.find((s) => s.id === subId);
    if (!sub) return;
    if (!window.confirm(translate("ConfirmCancelSubscription"))) return;

    setLoadingCancel(subId);
    try {
      // ne modifie que le champ cancel_at_period_end
      await updateDoc(sub.ref, { cancel_at_period_end: true });
      console.log(`🗑️ Subscription ${subId} scheduled for cancellation`);
    } catch (e) {
      console.error("❌ Error cancelling subscription", e);
    } finally {
      setLoadingCancel(null);
    }
  };

  return (
    <Box p={2} sx={{ bgcolor: "background.default", color: "text.primary" }}>
      <Typography variant="h5" gutterBottom>
        {translate("AllSubscriptions")}
      </Typography>

      <List>
        {subscriptions.map((sub) => (
          <Paper
            key={sub.ref.path}
            variant="outlined"
            sx={{
              my: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: "background.paper",
              borderColor: "divider",
            }}
          >
            <Box flexGrow={1}>
              <ListItemText
                primary={`${translate("Subscription")} #${sub.id}`}
                primaryTypographyProps={{ color: "text.primary" }}
                secondary={
                  <>
                    <div>
                      {translate("Customer")}: {sub.customerId}
                    </div>
                    <div>
                      {translate("Status")}: {sub.status}
                    </div>
                    {sub.price && (
                      <div>
                        {translate("Price")}:{" "}
                        {(sub.price.unit_amount / 100).toFixed(2)}/
                        {sub.price.interval}
                      </div>
                    )}
                    <div>
                      {translate("CreatedAt")}:{" "}
                      {new Date(sub.created * 1000).toLocaleString()}
                    </div>
                    {/* {sub.cancel_at_period_end && (
                      <div style={{ color: "orange" }}>
                        {translate("CancelScheduled")}
                      </div>
                    )} */}
                    {sub.cancel_at_period_end && (
                      <Typography
                        variant="body2"
                        sx={{ color: "warning.main" }}
                      >
                        {translate("CancelScheduled")}
                      </Typography>
                    )}
                  </>
                }
                secondaryTypographyProps={{ color: "text.secondary" }}
              />
            </Box>

            <Button
              variant="contained"
              color="error"
              onClick={() => handleCancel(sub.id)}
              disabled={loadingCancel === sub.id || !!sub.cancel_at_period_end}
            >
              {loadingCancel === sub.id ? (
                <CircularProgress size={20} />
              ) : sub.cancel_at_period_end ? (
                translate("CancelScheduled")
              ) : (
                translate("CancelNow")
              )}
            </Button>
          </Paper>
        ))}
      </List>
    </Box>
  );
}
